import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 120; // ~5 min

/**
 * Страница после редиректа со Stripe (success_url).
 * Рекомендуемый порядок: показываем "Processing payment…" и ждём успешного статуса.
 * Опрашиваем GET /api/billing/checkout-status; при status === "completed" — считаем успех и refreshUser().
 * Дополнительно опрашиваем GET /api/auth/me: если subscriptionPlan уже PRO — тоже считаем успех (fallback).
 * PRO в UI включается только после того, как бэкенд вернёт completed (бэкенд должен выставлять completed
 * только после обработки invoice.payment_succeeded).
 */
const BillingSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<'pending' | 'completed' | 'unknown'>(
    sessionId ? 'pending' : 'unknown'
  );
  const [error, setError] = useState<string | null>(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId || status !== 'pending') return;

    const check = async () => {
      pollCount.current += 1;
      if (pollCount.current > MAX_POLL_ATTEMPTS) {
        setError('Verification is taking longer than usual. Check your billing page or try again.');
        setStatus('unknown');
        return;
      }
      try {
        const res = await apiService.getCheckoutStatus(sessionId);
        if (res.status === 'completed') {
          setStatus('completed');
          await refreshUser();
          return;
        }
        // Fallback: раз в ~10s проверяем /me (бэкенд мог выставить PRO до обновления checkout-status)
        if (pollCount.current % 4 === 0) {
          const me = await apiService.getMe();
          const plan = me?.subscriptionPlan;
          if (plan === 'PRO' || plan === 'PREMIUM') {
            setStatus('completed');
            await refreshUser();
            return;
          }
        }
      } catch (e: any) {
        setError(e?.message || 'Could not verify payment status.');
        setStatus('unknown');
      }
    };

    const t = setInterval(check, POLL_INTERVAL_MS);
    check(); // first call immediately
    return () => clearInterval(t);
  }, [sessionId, status, refreshUser]);

  // Прямой заход без session_id — считаем успехом (или можно показать "нет сессии")
  useEffect(() => {
    if (!sessionId && status === 'unknown') {
      refreshUser();
    }
  }, [sessionId, status, refreshUser]);

  if (status === 'pending') {
    return (
      <div className="content">
        <div className="container mx-auto px-4 py-12 max-w-2xl text-center">
          <div
            className="billing-success-card billing-success-card--pending rounded-2xl p-8 border w-full max-w-lg mx-auto"
          >
            <div className="flex justify-center mb-6">
              <div
                className="billing-success-loader-wrap w-16 h-16 rounded-full flex items-center justify-center"
              >
                <Loader2 className="w-10 h-10 animate-spin" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Processing payment…</h1>
            <p className="text-white/80 mb-2">
              We're confirming your payment. This usually takes a few seconds.
            </p>
            <p className="text-white/50 text-sm">Please don't close this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <div className="container mx-auto px-4 py-12 max-w-2xl text-center">
          <div className="card-glass billing-success-error-card p-8 rounded-2xl">
            <p className="text-white/90 mb-4">{error}</p>
            <Link to="/billing" className="btn btn-gold inline-flex items-center gap-2">
              Go to Billing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // status === 'completed' или заход без session_id — показываем успех
  return (
    <div className="content">
      <div className="container mx-auto px-4 py-12 max-w-2xl text-center">
        <div
          className="billing-success-card billing-success-card--completed rounded-2xl p-8 border w-full max-w-lg mx-auto"
        >
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-brand-gold billing-success-check-icon" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Payment successful</h1>
          <p className="text-white/80 mb-6">
            Your Premium subscription is now active. You can use all Premium features.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/dashboard" className="btn btn-gold inline-flex items-center justify-center gap-2">
              Go to Dashboard
            </Link>
            <Link
              to="/billing"
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/20 transition inline-flex items-center justify-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              Billing &amp; History
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingSuccess;
